import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePermission } from "../context/PermissionContext";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,500;1,600&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .ish-root {
    min-height: 100vh;
    height: 100vh;
    min-height: 100dvh;
    height: 100dvh;
    width: 100vw;
    display: flex;
    align-items: stretch;
    font-family: 'DM Sans', sans-serif;
    background: #0A0F1F;
    overflow: clip;
  }

  /* ══════════════════════════════
     LEFT PANEL — JEWELLERY HERO
  ══════════════════════════════ */
  .ish-left {
    width: 50%;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 56px 60px 56px;
    background: #0A0F1F;
  }
  .ish-left-bg {
    position: absolute;
    inset: 0;
    background-image: url('/neck1.png');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    z-index: 0;
    animation: ishZoom 22s ease-in-out infinite alternate;
  }
  @keyframes ishZoom {
    0%   { transform: scale(1); }
    100% { transform: scale(1.08); }
  }
  .ish-left-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(6,9,24,0.35) 0%,
      rgba(6,9,24,0.35) 30%,
      rgba(6,9,24,0.88) 68%,
      rgba(6,9,24,0.97) 100%
    );
    z-index: 1;
  }
  .ish-left-content {
    position: relative;
    z-index: 2;
    max-width: 440px;
    opacity: 0;
    animation: ishSlideUp 0.9s 0.15s cubic-bezier(0.16,1,0.3,1) forwards;
  }
  @keyframes ishSlideUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Logo */
  .ish-logo {
    position: absolute;
    top: 48px;
    left: 60px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    opacity: 0;
    animation: ishFadeIn 1s 0.1s ease forwards;
  }
  @media (max-width: 780px) {
    .ish-logo { top: 32px; left: 28px; }
  }
  @media (max-width: 480px) {
    .ish-logo { top: 26px; left: 22px; }
  }
  @keyframes ishFadeIn { to { opacity: 1; } }
  .ish-logo-crown {
    width: 22px; height: 22px;
    color: #D4AF37;
    margin-bottom: 4px;
  }
  .ish-logo-name {
    font-family: 'Playfair Display', serif;
    font-size: 26px;
    letter-spacing: 6px;
    color: #ffffff;
    font-weight: 600;
    line-height: 1;
  }
  .ish-logo-sub {
    font-size: 10px;
    letter-spacing: 5px;
    color: #D4AF37;
    font-weight: 500;
    margin-top: 5px;
  }

  .ish-eyebrow {
    font-size: 11.5px; font-weight: 600;
    letter-spacing: 3px; text-transform: uppercase;
    color: #D4AF37;
    margin-bottom: 10px;
  }
  .ish-eyebrow-rule {
    width: 34px; height: 1.5px;
    background: #D4AF37;
    margin-bottom: 22px;
    opacity: 0.8;
  }
  .ish-headline {
    font-family: 'Playfair Display', serif;
    font-size: 27px; font-weight: 500;
    color: #ffffff; line-height: 1.15;
    margin-bottom: 2px;
  }
  .ish-headline-main {
    font-family: 'Playfair Display', serif;
    font-style: italic;
    font-size: 42px; font-weight: 600;
    line-height: 1.18;
    background: linear-gradient(120deg, #F3D07A 0%, #D4AF37 55%, #B8860B 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 20px;
  }
  .ish-left-desc {
    font-size: 14.5px;
    color: rgba(255,255,255,0.62);
    line-height: 1.7;
    font-weight: 400;
    max-width: 380px;
    margin-bottom: 40px;
  }

  .ish-features {
    display: flex;
    gap: 30px;
  }
  .ish-feature {
    display: flex;
    align-items: center;
    gap: 9px;
  }
  .ish-feature-icon {
    width: 17px; height: 17px;
    color: #D4AF37;
    flex-shrink: 0;
  }
  .ish-feature-text {
    font-size: 12.5px;
    color: rgba(255,255,255,0.82);
    line-height: 1.35;
    font-weight: 400;
  }

  /* ══════════════════════════════
     RIGHT PANEL — LOGIN CARD
  ══════════════════════════════ */
  .ish-right {
    flex: 1;
    background:
      radial-gradient(ellipse 480px 380px at 85% 15%, rgba(212,175,55,0.05) 0%, transparent 70%),
      radial-gradient(ellipse 380px 380px at 10% 90%, rgba(212,175,55,0.04) 0%, transparent 70%),
      #0A0F1F;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    position: relative;
  }
  .ish-card {
    width: 100%; max-width: 440px;
    background: linear-gradient(160deg, #131A30 0%, #0D1224 100%);
    border: 1px solid rgba(212,175,55,0.28);
    border-radius: 20px;
    padding: 44px 44px 36px;
    box-shadow: 0 30px 70px rgba(0,0,0,0.45), 0 0 0 1px rgba(212,175,55,0.04) inset;
    opacity: 0;
    animation: ishCardIn 0.8s 0.2s cubic-bezier(0.16,1,0.3,1) forwards;
  }
  @keyframes ishCardIn {
    from { opacity: 0; transform: translateY(16px) scale(0.985); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .ish-icon-row {
    display: flex; align-items: center; justify-content: center;
    gap: 14px; margin-bottom: 22px;
  }
  .ish-icon-line { flex: 1; max-width: 90px; height: 1px; background: rgba(212,175,55,0.3); }
  .ish-avatar-badge {
    width: 54px; height: 54px; border-radius: 50%;
    border: 1.5px solid #D4AF37;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    background: rgba(212,175,55,0.06);
  }
  .ish-avatar-badge svg { width: 24px; height: 24px; color: #D4AF37; }

  .ish-card-heading {
    text-align: center;
    font-family: 'Playfair Display', serif;
    font-size: 27px; font-weight: 600;
    color: #F4F1EA;
    margin-bottom: 9px;
  }
  .ish-card-heading span { color: #D4AF37; }
  .ish-card-subhead {
    text-align: center;
    font-size: 13px; color: rgba(244,241,234,0.55);
    font-weight: 400; margin-bottom: 30px; line-height: 1.55;
  }

  /* Alert */
  .ish-alert {
    width: 100%; padding: 11px 15px; border-radius: 10px;
    font-size: 13px; font-weight: 500;
    display: flex; align-items: center; gap: 8px; margin-bottom: 18px;
  }
  .ish-alert.error   { background: rgba(220,80,80,0.1); color: #F19A9A; border: 1px solid rgba(220,80,80,0.28); }
  .ish-alert.success { background: rgba(80,200,140,0.1); color: #7FDDB0; border: 1px solid rgba(80,200,140,0.28); }

  /* Fields */
  .ish-field { width: 100%; margin-bottom: 16px; }
  .ish-label {
    display: block; font-size: 10.5px; font-weight: 700;
    color: #D4AF37; letter-spacing: 2px;
    text-transform: uppercase; margin-bottom: 8px;
  }
  .ish-input-wrap {
    position: relative; display: flex; align-items: center;
    background: rgba(255,255,255,0.03);
    border: 1.3px solid rgba(212,175,55,0.22);
    border-radius: 11px; padding: 0 14px;
    transition: all 0.22s ease;
  }
  .ish-input-wrap:focus-within {
    border-color: #D4AF37;
    background: rgba(212,175,55,0.05);
    box-shadow: 0 0 0 4px rgba(212,175,55,0.09), 0 4px 18px rgba(212,175,55,0.1);
  }
  .ish-input-icon {
    width: 16px; height: 16px; color: #B8963F;
    flex-shrink: 0; margin-right: 11px; transition: color 0.2s;
  }
  .ish-input-wrap:focus-within .ish-input-icon { color: #D4AF37; }
  .ish-input {
    flex: 1;
    width: 100%;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: none;
    border-radius: 0;
    background-color: #0f172d !important;
    color: #F4F1EA;
    font-size: 13.5px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    outline: none;
    letter-spacing: 0.1px;
    appearance: none;
    -webkit-appearance: none;
    box-shadow: inset 0 0 0 1000px #0f172d;
    caret-color: #F0C24B;
  }
  .ish-input:focus {
    background-color: #0f172d !important;
    color: #ffffff;
    box-shadow: inset 0 0 0 1000px #0f172d;
  }
  .ish-input::selection {
    background: rgba(240, 194, 75, 0.34);
    color: #ffffff;
  }
  .ish-input::placeholder { color: rgba(244,241,234,0.32); font-weight: 300; }

  .ish-eye-btn {
    background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    padding: 4px; color: #B8963F; transition: color 0.18s, transform 0.18s; flex-shrink: 0;
  }
  .ish-eye-btn:hover { color: #D4AF37; transform: scale(1.08); }
  .ish-eye-btn svg { width: 18px; height: 18px; }

  .ish-row {
    width: 100%; display: flex; align-items: center;
    justify-content: space-between; margin-bottom: 24px;
  }
  .ish-remember {
    display: flex; align-items: center; gap: 7px;
    font-size: 12.5px; color: rgba(244,241,234,0.65);
    cursor: pointer; font-weight: 400; user-select: none;
  }
  .ish-remember input { accent-color: #D4AF37; width: 14px; height: 14px; }
  .ish-forgot {
    font-size: 12.5px; color: #D4AF37; font-weight: 600;
    cursor: pointer; transition: color 0.15s;
  }
  .ish-forgot:hover { color: #F3D07A; }

  .ish-btn {
    width: 100%; padding: 15px;
    background: linear-gradient(120deg, #F3D07A 0%, #D4AF37 55%, #B8860B 100%);
    border: none; border-radius: 12px;
    color: #1a1305; font-weight: 700; font-size: 13.5px;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer; letter-spacing: 1.6px; text-transform: uppercase;
    position: relative; overflow: hidden;
    transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
    box-shadow: 0 10px 30px rgba(212,175,55,0.28);
  }
  .ish-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(212,175,55,0.42);
  }
  .ish-btn:active:not(:disabled) { transform: translateY(0); }
  .ish-btn:disabled { opacity: 0.62; cursor: not-allowed; }
  .ish-btn::after {
    content: ''; position: absolute; top: 0; left: -100%;
    width: 60%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
    transition: left 0.5s;
  }
  .ish-btn:hover::after { left: 160%; }
  .ish-btn-inner {
    display: flex; align-items: center;
    justify-content: center; gap: 9px; position: relative; z-index: 1;
  }
  .ish-btn-inner svg { width: 15px; height: 15px; transition: transform 0.18s; }
  .ish-btn:hover:not(:disabled) .ish-btn-inner svg { transform: translateX(3px); }
  .ish-spinner {
    width: 15px; height: 15px;
    border: 2.5px solid rgba(26,19,5,0.3);
    border-top-color: #1a1305; border-radius: 50%;
    animation: ishSpin 0.7s linear infinite; flex-shrink: 0;
  }
  @keyframes ishSpin { to { transform: rotate(360deg); } }

  .ish-divider {
    display: flex; align-items: center; gap: 14px;
    margin: 22px 0;
  }
  .ish-divider-line { flex: 1; height: 1px; background: rgba(212,175,55,0.18); }
  .ish-divider-text { font-size: 11px; color: rgba(244,241,234,0.4); letter-spacing: 1.5px; }


  .ish-trust {
    display: flex; align-items: center; justify-content: center;
    gap: 16px; margin-top: 26px; padding-top: 20px;
    border-top: 1px solid rgba(212,175,55,0.14);
  }
  .ish-trust-item {
    display: flex; align-items: center; gap: 5px;
    font-size: 10.5px; color: rgba(244,241,234,0.42); font-weight: 500;
  }
  .ish-trust-item svg { width: 13px; height: 13px; flex-shrink: 0; color: #B8963F; }

  /* Responsive */
  @media (max-width: 980px) {
    .ish-left { width: 46%; padding: 44px 40px; }
    .ish-headline-main { font-size: 34px; }
    .ish-card { padding: 36px 30px 30px; }
  }
  @media (max-width: 780px) {
    .ish-root { flex-direction: column; }
    .ish-left {
      width: 100%; min-height: 260px; padding: 32px 28px 28px;
    }
    .ish-features { flex-wrap: wrap; row-gap: 12px; }
    .ish-left-desc { margin-bottom: 22px; }
    .ish-headline-main { font-size: 30px; }
    .ish-right { padding: 32px 20px 48px; }
  }
  @media (max-width: 480px) {
    .ish-left { min-height: 0; padding: 26px 22px 24px; }
    .ish-left-desc { display: none; }
    .ish-features { display: none; }
    .ish-headline { font-size: 20px; }
    .ish-headline-main { font-size: 26px; margin-bottom: 0; }
    .ish-card { padding: 30px 22px 26px; border-radius: 16px; }
  }
  /* Login page reference layout override */
  .ish-root {
    min-height: 100vh;
    min-height: 100dvh;
    background: #070d1c;
  }

  .ish-left {
    width: 50%;
    min-height: 100vh;
    height: 100vh;
    min-height: 100dvh;
    height: 100dvh;
    padding: 40px 50px 44px;
    justify-content: space-between;
    border-right: 1px solid rgba(212, 175, 55, 0.22);
    background: #050b18;
    overflow: hidden;
  }

  .ish-left-bg {
    background-image: url('/neck1.png');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    opacity: 1;
    filter: saturate(1.1) contrast(1.08);
    transform-origin: center center;
  }

  .ish-left-overlay {
    background:
      radial-gradient(ellipse 52% 40% at 60% 55%, rgba(212, 175, 55, 0.08), transparent 65%),
      linear-gradient(90deg, rgba(5, 11, 24, 0.68) 0%, rgba(5, 11, 24, 0.32) 54%, rgba(5, 11, 24, 0.16) 100%),
      linear-gradient(180deg, rgba(5, 11, 24, 0.5) 0%, rgba(5, 11, 24, 0.12) 50%, rgba(5, 11, 24, 0.86) 100%);
  }

  .ish-logo {
    position: relative;
    top: auto;
    left: auto;
    margin-bottom: auto;
  }

  .ish-logo-crown {
    width: 20px;
    height: 20px;
    margin-left: 2px;
    color: #F0C24B;
  }

  .ish-logo-name {
    font-size: 30px;
    letter-spacing: 8px;
    color: #F8FAFC;
    text-shadow: 0 3px 18px rgba(0, 0, 0, 0.28);
  }

  .ish-logo-sub {
    color: #F0C24B;
    letter-spacing: 7px;
    font-weight: 700;
  }

  .ish-left-content {
    max-width: 620px;
    margin-top: auto;
  }

  .ish-eyebrow {
    display: none;
  }

  .ish-eyebrow-rule {
    width: 90px;
    height: 2px;
    margin: 22px 0 28px;
    background: linear-gradient(90deg, #F0C24B, rgba(240, 194, 75, 0));
  }

  .ish-headline {
    font-size: clamp(31px, 3vw, 44px);
    font-weight: 700;
    color: #ffffff;
    text-shadow: 0 8px 26px rgba(0, 0, 0, 0.36);
  }

  .ish-headline-main {
    font-size: clamp(46px, 5.1vw, 68px);
    margin-bottom: 28px;
    background: linear-gradient(120deg, #FFE08A 0%, #F0C24B 50%, #D49A1E 100%);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .ish-left-desc {
    max-width: 560px;
    color: rgba(255, 255, 255, 0.82);
    font-size: 18px;
    line-height: 1.65;
    margin-bottom: 54px;
    font-weight: 600;
  }

  .ish-features {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    max-width: 100%;
  }

  .ish-feature {
    min-height: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 10px;
    padding: 0 16px 0 0;
    border-right: 1px solid rgba(212, 175, 55, 0.18);
  }

  .ish-feature:last-child {
    border-right: none;
  }

  .ish-feature-icon {
    width: 20px;
    height: 20px;
    color: #F0C24B;
  }

  .ish-feature-text {
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
  }

  .ish-feature-text small {
    display: block;
    margin-top: 4px;
    color: rgba(255, 255, 255, 0.68);
    font-size: 10px;
    line-height: 1.4;
    font-weight: 500;
  }

  .ish-right {
    min-height: 100vh;
    height: 100vh;
    min-height: 100dvh;
    height: 100dvh;
    padding: 24px 50px;
    background:
      radial-gradient(ellipse 560px 420px at 78% 18%, rgba(22, 48, 89, 0.32), transparent 70%),
      radial-gradient(ellipse 420px 360px at 16% 82%, rgba(212, 175, 55, 0.06), transparent 70%),
      #070d1c;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: clip;
  }

  .ish-card {
    max-width: 520px;
    max-height: calc(100vh - 48px);
    max-height: calc(100dvh - 48px);
    padding: 34px 46px 28px;
    border-radius: 28px;
    border: 1.4px solid rgba(240, 194, 75, 0.72);
    background: linear-gradient(145deg, rgba(18, 28, 55, 0.94), rgba(9, 16, 33, 0.98));
    box-shadow: 0 34px 86px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.04);
  }

  .ish-icon-row { margin-bottom: 18px; gap: 22px; }
  .ish-icon-line { max-width: 120px; background: linear-gradient(90deg, transparent, rgba(240, 194, 75, 0.42), transparent); }
  .ish-avatar-badge { width: 62px; height: 62px; border-color: rgba(240, 194, 75, 0.7); }
  .ish-avatar-badge svg { width: 28px; height: 28px; color: #F0C24B; }
  .ish-card-heading { font-size: 31px; margin-bottom: 8px; }
  .ish-card-subhead { font-size: 14px; color: rgba(244, 241, 234, 0.76); margin-bottom: 20px; }
  .ish-field { margin-bottom: 16px; }
  .ish-label { font-size: 12px; letter-spacing: 2.5px; margin-bottom: 8px; color: #F0C24B; }
  .ish-input-wrap { height: 52px; border-radius: 12px; padding: 0 16px; border-color: rgba(240, 194, 75, 0.34); background: rgba(7, 13, 28, 0.74); overflow: hidden; }
  .ish-input { padding: 0; font-size: 14px; }
  .ish-input:-webkit-autofill,
  .ish-input:-webkit-autofill:hover,
  .ish-input:-webkit-autofill:focus,
  .ish-input:-internal-autofill-selected {
    -webkit-text-fill-color: #F4F1EA !important;
    -webkit-box-shadow: 0 0 0 1000px #0f172d inset !important;
    box-shadow: 0 0 0 1000px #0f172d inset !important;
    background-color: #0f172d !important;
    caret-color: #F0C24B;
    transition: background-color 9999s ease-in-out 0s;
  }
  .ish-input-icon { width: 18px; height: 18px; margin-right: 14px; color: #E1AE2E; }
  .ish-eye-btn svg { width: 20px; height: 20px; }
  .ish-row { margin-bottom: 20px; }
  .ish-remember, .ish-forgot { font-size: 13px; }
  .ish-btn { min-height: 54px; border-radius: 12px; font-size: 13.5px; letter-spacing: 2px; background: linear-gradient(120deg, #FFE08A 0%, #F0C24B 46%, #D49A1E 100%); }
  .ish-divider { display: none; }
  .ish-trust { margin-top: 18px; padding-top: 14px; border-top: 1px solid rgba(212, 175, 55, 0.14); }
  .ish-trust-item { font-size: 12px; color: rgba(244, 241, 234, 0.64); }
  .ish-trust-item:not(:first-child) { display: none; }
  .ish-trust-item:first-child::after { content: ' SSL Secured login protected by ISHMAI'; }

  @media (max-width: 1100px) {
    .ish-left { padding: 36px 32px 40px; }
    .ish-right { padding: 22px 32px; }
    .ish-card { padding: 30px 32px 26px; }
    .ish-features { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
    .ish-feature { min-height: auto; border-right: 1px solid rgba(212, 175, 55, 0.18); padding: 0 12px 0 0; }
    .ish-feature:last-child { border-right: none; }
  }

  @media (max-width: 780px) {
    .ish-root { overflow-y: auto; }
    .ish-left { width: 100%; min-height: 360px; height: auto; padding: 32px 24px; }
    .ish-left-bg { background-size: cover; background-position: center center; }
    .ish-left-desc { margin-bottom: 20px; }
    .ish-features { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 4px; }
    .ish-feature { padding: 0 8px 0 0; }
    .ish-right { min-height: auto; height: auto; padding: 28px 20px 32px; }
    .ish-card { max-width: 100%; padding: 32px 20px 24px; }
    .ish-card-heading { font-size: 28px; }
    .ish-input-wrap { height: 50px; }
  }

  @media (max-height: 720px) and (min-width: 781px) {
    .ish-left { padding-top: 34px; padding-bottom: 34px; }
    .ish-logo-name { font-size: 27px; }
    .ish-logo-sub { letter-spacing: 6px; }
    .ish-feature { gap: 8px; }
    .ish-feature-icon { width: 18px; height: 18px; }
    .ish-feature-text { font-size: 11px; }
    .ish-feature-text small { font-size: 9.5px; line-height: 1.3; }
    .ish-right { padding-top: 18px; padding-bottom: 18px; }
    .ish-card {
      max-height: calc(100vh - 36px);
      max-height: calc(100dvh - 36px);
      padding: 26px 44px 22px;
      border-radius: 24px;
    }
    .ish-icon-row { margin-bottom: 14px; }
    .ish-avatar-badge { width: 54px; height: 54px; }
    .ish-avatar-badge svg { width: 24px; height: 24px; }
    .ish-card-heading { font-size: 28px; margin-bottom: 6px; }
    .ish-card-subhead { font-size: 13px; line-height: 1.45; margin-bottom: 16px; }
    .ish-field { margin-bottom: 13px; }
    .ish-label { font-size: 11px; margin-bottom: 6px; }
    .ish-input-wrap { height: 48px; }
    .ish-row { margin-bottom: 16px; }
    .ish-btn { min-height: 50px; }
    .ish-trust { margin-top: 14px; padding-top: 12px; }
  }
  @media (max-width: 480px) {
    .ish-left { min-height: 300px; padding: 24px 18px; }
    .ish-logo-name { font-size: 22px; letter-spacing: 5px; }
    .ish-features { display: none; }
    .ish-card { padding: 28px 18px 22px; border-radius: 18px; }
    .ish-card-heading { font-size: 26px; }
    .ish-input-wrap { height: 48px; padding: 0 12px; }
    .ish-icon-row { margin-bottom: 16px; }
    .ish-avatar-badge { width: 58px; height: 58px; }
  }

  /* Login-only override for the global admin input theme. */
  html body #root .ish-root .ish-input-wrap > input.ish-input,
  html body #root .ish-root .ish-input-wrap > input.ish-input:hover,
  html body #root .ish-root .ish-input-wrap > input.ish-input:focus,
  html body #root .ish-root .ish-input-wrap > input.ish-input:active {
    min-height: 0 !important;
    height: 100% !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    background-color: transparent !important;
    color: #F4F1EA !important;
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
    outline: none !important;
    caret-color: #F0C24B !important;
  }

  html body #root .ish-root .ish-input-wrap > input.ish-input::placeholder {
    color: rgba(244, 241, 234, 0.52) !important;
    opacity: 1 !important;
  }

  html body #root .ish-root .ish-input-wrap > input.ish-input:-webkit-autofill,
  html body #root .ish-root .ish-input-wrap > input.ish-input:-webkit-autofill:hover,
  html body #root .ish-root .ish-input-wrap > input.ish-input:-webkit-autofill:focus,
  html body #root .ish-root .ish-input-wrap > input.ish-input:-webkit-autofill:active {
    -webkit-text-fill-color: #F4F1EA !important;
    -webkit-box-shadow: 0 0 0 1000px #0f172d inset !important;
    box-shadow: 0 0 0 1000px #0f172d inset !important;
    background-color: #0f172d !important;
    border: 0 !important;
    caret-color: #F0C24B !important;
    transition: background-color 9999s ease-in-out 0s !important;
  }
  @media (prefers-reduced-motion: reduce) {
    .ish-left-bg, .ish-left-content, .ish-card { animation: none !important; opacity: 1 !important; transform: none !important; }
  }
`;

/* ── Icons ───────────────────────────── */
const CrownIcon = () => (
  <svg className="ish-logo-crown" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2 20h20l-1.6-9.5-4.9 4-3.5-8-3.5 8-4.9-4L2 20z" />
  </svg>
);
const AdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c0-3.6 3.1-6.5 7-6.5s7 2.9 7 6.5" strokeLinecap="round" />
  </svg>
);
const MailIcon = () => (
  <svg className="ish-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 6.5l8.5 6 8.5-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const LockIcon = () => (
  <svg className="ish-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <rect x="4" y="10.5" width="16" height="10" rx="2" />
    <path d="M7.5 10.5V7a4.5 4.5 0 0 1 9 0v3.5" strokeLinecap="round" />
  </svg>
);
const EyeOpenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const DiamondIcon = () => (
  <svg className="ish-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M6 3h12l4 5-10 13L2 8l4-5z" />
    <path d="M2 8h20M9 3l3 5-3 13M15 3l-3 5 3 13" strokeLinecap="round" />
  </svg>
);
const ShieldIcon = () => (
  <svg className="ish-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M12 2L4 6v6c0 5.1 3.4 9.9 8 11 4.6-1.1 8-5.9 8-11V6l-8-4z" />
    <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CrownFeatureIcon = () => (
  <svg className="ish-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M2 20h20l-1.6-9.5-4.9 4-3.5-8-3.5 8-4.9-4L2 20z" />
  </svg>
);
const Login = () => {
  const navigate = useNavigate();
  const { login: loginAdmin } = useAuth();
  const { refreshPermissions } = usePermission();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    localStorage.removeItem("adminEmail");
    localStorage.removeItem("adminRemember");
  }, []);

  const validate = () => {
    if (!email || !password) {
      setMsg({ type: "error", text: "Please fill in all fields." });
      return false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setMsg({ type: "error", text: "Please enter a valid email address." });
      return false;
    }
    if (password.length < 6) {
      setMsg({ type: "error", text: "Password must be at least 6 characters." });
      return false;
    }
    return true;
  };

  const login = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      setMsg({ type: "", text: "" });
      const data = await loginAdmin(email, password, remember);
      if (data && (data.success || data.token)) {
        setMsg({ type: "success", text: "Login successful! Redirecting…" });
        if (typeof refreshPermissions === 'function') {
          await refreshPermissions(true).catch(() => null);
        }
        navigate("/admin/dashboard");
      } else {
        setMsg({ type: "error", text: (data && data.message) || "Invalid credentials" });
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: (err.response && err.response.data && (err.response.data.message || err.response.data.error)) || err.message || "Server error (check backend running on port 5000)",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") login(); };

  return (
    <>
      <style>{CSS}</style>
      <div className="ish-root">

        {/* ══ LEFT PANEL ══ */}
        <div className="ish-left">
          <div className="ish-left-bg" />
          <div className="ish-left-overlay" />

          <div className="ish-logo">
            <CrownIcon />
            <div className="ish-logo-name">ISHMAI</div>
            <div className="ish-logo-sub">JEWELLERY</div>
          </div>

          <div className="ish-left-content">
            {/* <div className="ish-eyebrow">Timeless Beauty. Precious Trust.</div>
            <div className="ish-eyebrow-rule" />
            <div className="ish-headline">Welcome to</div>
            <div className="ish-headline-main">ISHMAI Admin</div> */}
            {/* <p className="ish-left-desc">
              Manage your jewellery business effortlessly — products, orders,
              customers and more.
            </p> */}

            <div className="ish-features">
              <div className="ish-feature">
                <DiamondIcon />
                <span className="ish-feature-text">Secure & Reliable<small>Your data is protected with top-tier security</small></span>
              </div>
              <div className="ish-feature">
                <ShieldIcon />
                <span className="ish-feature-text">Business Insights<small>Track performance and grow your business</small></span>
              </div>
              <div className="ish-feature">
                <CrownFeatureIcon />
                <span className="ish-feature-text">Easy Management<small>Manage products, orders and customers easily</small></span>
              </div>
            </div>
          </div>
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div className="ish-right">
          <div className="ish-card">

            <div className="ish-icon-row">
              <div className="ish-icon-line" />
              <div className="ish-avatar-badge"><AdminIcon /></div>
              <div className="ish-icon-line" />
            </div>

            <div className="ish-card-heading">Admin <span>Login</span></div>
            <div className="ish-card-subhead">
              Welcome back! Please sign in to continue<br />to your account.
            </div>

            {msg.text && (
              <div className={"ish-alert " + msg.type}>
                {msg.type === "error" ? "⚠️" : "✅"} {msg.text}
              </div>
            )}

            <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); login(); }}>
              <input type="text" name="fakeusernameremembered" autoComplete="off" style={{ display: "none" }} />
              <input type="password" name="fakepasswordremembered" autoComplete="off" style={{ display: "none" }} />

              <div className="ish-field">
                <label className="ish-label">Email Address</label>
                <div className="ish-input-wrap">
                  <MailIcon />
                  <input
                    type="email"
                    name="email"
                    autoComplete="off"
                    className="ish-input"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>

              <div className="ish-field">
                <label className="ish-label">Password</label>
                <div className="ish-input-wrap">
                  <LockIcon />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    className="ish-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    className="ish-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeOpenIcon />}
                  </button>
                </div>
              </div>

              <div className="ish-row">
                <label className="ish-remember">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Remember me
                </label>
                <span className="ish-forgot" onClick={() => navigate("/admin/forgot-password")}>
                  Forgot password?
                </span>
              </div>

              <button className="ish-btn" type="submit" disabled={loading}>
                <span className="ish-btn-inner">
                  {loading ? (
                    <><span className="ish-spinner" />Signing in…</>
                  ) : (
                    <>Sign In <ArrowIcon /></>
                  )}
                </span>
              </button>
            </form>

            <div className="ish-trust">
              <div className="ish-trust-item">
                <ShieldIcon />
              </div>
            </div>

          </div>
        </div>

      </div>
    </>
  );
};

export default Login;




